import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { StageStatus } from '@iotv/io';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, timelineOppositeContentClasses, TimelineSeparator, } from '@mui/lab';
import { Alert, alertClasses, typographyClasses } from '@mui/material';
import { useContext } from 'react';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
export const stringToId = (s) => {
    const prefixed = 'id-' + s;
    const underscored = prefixed.replaceAll(/ /g, '_');
    const escaped = CSS.escape(underscored);
    return escaped;
};
const ResolveStatus = (status) => {
    switch (status) {
        case StageStatus.ERRORED:
            return 'error';
        case StageStatus.COMPLETED:
            return 'success';
        case StageStatus.INPROGRESS:
            return 'info';
        case StageStatus.UNSTARTED:
        default:
            return 'grey';
    }
};
const ComputeSeverity = (status) => {
    switch (status) {
        case StageStatus.ERRORED:
            return 'error';
        case StageStatus.COMPLETED:
            return 'success';
        case StageStatus.INPROGRESS:
            return 'info';
        case StageStatus.UNSTARTED:
        default:
            return 'unknown';
    }
};
const ComputeForeground = (theme, status) => {
    switch (status) {
        case StageStatus.ERRORED:
            return theme.palette.error.main;
        case StageStatus.COMPLETED:
            return theme.palette.success.main;
        case StageStatus.INPROGRESS:
            return theme.palette.info.main;
        case StageStatus.UNSTARTED:
        default:
            return theme.palette.text.primary;
    }
};
const ComputeBackground = (theme, status) => {
    switch (status) {
        case StageStatus.COMPLETED:
            return theme.palette.success.main;
        case StageStatus.ERRORED:
        case StageStatus.INPROGRESS:
        case StageStatus.UNSTARTED:
        default:
            return theme.palette.grey[400];
    }
};
const ComputeBefore = (theme, status) => {
    return {
        content: "''",
        position: 'absolute',
        inset: 0,
        opacity: status === StageStatus.INPROGRESS ? 1 : 0,
        backgroundImage: `linear-gradient(${theme.palette.info.main}, ${theme.palette.grey[400]})`,
        transition: 'opacity 1s ease-in-out',
    };
};
export const AnimatedTimeline = ({ stages }) => {
    const [{ isSmallView }] = useContext(AppMainViewContext);
    return (_jsx(Timeline, { position: "left", sx: { padding: 0, marginBlock: 0 }, children: stages.map((stage, i, arr) => {
            const { name, id, status, description } = stage;
            const severity = ComputeSeverity(status);
            const minHeight = isSmallView ? '2.5em' : '1em';
            const heightMultiplier = isSmallView ? '.35em' : '.5em';
            const maxHeight = isSmallView ? '12.5em' : '15em';
            return (_jsxs(TimelineItem, { sx: {
                    minHeight: `clamp(${minHeight}, calc(${stage.steps} * ${heightMultiplier}), ${maxHeight})`,
                    ['&:last-of-type']: {
                        minHeight: 0,
                    },
                }, children: [description && (_jsx(TimelineOppositeContent, { color: "text.secondary", sx: {
                            [`&.${typographyClasses.root}.${timelineOppositeContentClasses.root}`]: {
                                marginBlockStart: 'calc(11.5px + 4px + 2px)',
                                paddingBlock: 0,
                                paddingInlineEnd: 0,
                                position: 'relative',
                                flex: isSmallView ? '0.6' : '0.5',
                            },
                        }, children: _jsx(Alert, { severity: severity !== 'unknown' ? severity : undefined, icon: severity === 'unknown' ? _jsx(HelpOutlineIcon, { fontSize: "inherit" }) : undefined, sx: {
                                color: severity === 'unknown' ? (theme) => theme.palette.grey[600] : undefined,
                                backgroundColor: severity === 'unknown' ? (theme) => theme.palette.grey[200] : undefined,
                                transition: 'background-color 0.5s ease-in-out',
                                display: isSmallView ? 'flex' : 'inline-flex',
                                transform: 'translateY(-50%)',
                                paddingBlock: 0.5,
                                paddingInline: 1,
                                alignItems: 'center',
                                [`& .${alertClasses.icon}`]: {
                                    paddingBlock: 0,
                                    color: severity === 'unknown' ? (theme) => theme.palette.grey[500] : undefined,
                                    transition: 'color 0.5s ease-in-out',
                                },
                                [`& .${alertClasses.message}`]: {
                                    paddingBlock: 0,
                                    transition: 'color 0.5s ease-in-out',
                                },
                            }, children: description }) })), _jsxs(TimelineSeparator, { children: [_jsx(TimelineDot, { color: ResolveStatus(status), sx: { transition: 'background-color 0.5s ease-out' } }), i !== arr.length - 1 && (_jsx(TimelineConnector, { sx: {
                                    marginBlock: '-0.5em',
                                    position: 'relative',
                                    backgroundColor: (theme) => ComputeBackground(theme, status),
                                    '::before': (theme) => ComputeBefore(theme, status),
                                } }))] }), _jsx(TimelineContent, { id: stringToId(id), sx: {
                            color: (theme) => ComputeForeground(theme, status),
                            transition: 'color 0.5s ease-out',
                            paddingInlineStart: 0,
                            flex: isSmallView ? '0.4' : '0.5',
                        }, children: name })] }, name));
        }) }));
};
